import React, { useState, useEffect } from 'react';
import api from '../api';
import {
  File, FileArrowUp, Trash, SpinnerGap, CheckCircle, Warning, Eye,
  FilePdf, FileDoc, FileImage, FileText, ShieldCheck
} from '@phosphor-icons/react';

const FILE_ICONS = { pdf: FilePdf, doc: FileDoc, docx: FileDoc, jpg: FileImage, jpeg: FileImage, png: FileImage, txt: FileText };

function getFileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  return FILE_ICONS[ext] || File;
}

function HealthBar({ pct }) {
  const color = pct >= 80 ? '#2D7D46' : pct >= 50 ? '#D9943B' : '#C6493A';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-surface-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-[9px] font-bold" style={{ color }}>{pct}%</span>
    </div>
  );
}

export default function EvidenceHub({ objectives }) {
  const [summary, setSummary] = useState([]);
  const [selectedObj, setSelectedObj] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [desc, setDesc] = useState('');
  const [evType, setEvType] = useState('');

  useEffect(() => {
    api.get('/api/env/evidence-summary').then(r => { setSummary(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const loadEvidence = async (objId) => {
    setSelectedObj(objId);
    setLoadingEvidence(true);
    try {
      const resp = await api.get(`/api/env/evidence/${objId}`);
      setEvidence(resp.data);
    } catch { setEvidence([]); }
    setLoadingEvidence(false);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedObj) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('linked_record', selectedObj);
    formData.append('linked_type', 'env_objective');
    formData.append('evidence_type', evType || 'supporting_document');
    formData.append('description', desc || file.name);
    try {
      await api.post('/api/evidence/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadEvidence(selectedObj);
      // Refresh summary
      const resp = await api.get('/api/env/evidence-summary');
      setSummary(resp.data);
      setDesc('');
      setEvType('');
    } catch {}
    setUploading(false);
    e.target.value = '';
  };

  const deleteEvidence = async (id) => {
    try {
      await api.delete(`/api/evidence/${id}`);
      setEvidence(prev => prev.filter(e => e.id !== id));
      const resp = await api.get('/api/env/evidence-summary');
      setSummary(resp.data);
    } catch {}
  };

  if (loading) return <div className="flex items-center justify-center py-16"><SpinnerGap size={24} className="animate-spin text-brand-primary" /></div>;

  const totalEvidence = summary.reduce((a, b) => a + b.evidence_count, 0);
  const avgComplete = summary.length > 0 ? Math.round(summary.reduce((a, b) => a + b.completeness_pct, 0) / summary.length) : 0;

  return (
    <div data-testid="evidence-hub" className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-border-default rounded-lg p-3.5">
          <p className="text-2xl font-heading font-bold text-brand-primary">{totalEvidence}</p>
          <p className="text-[10px] text-content-secondary font-medium">Total Evidence Files</p>
        </div>
        <div className="bg-white border border-border-default rounded-lg p-3.5">
          <p className="text-2xl font-heading font-bold text-status-on-track">{avgComplete}%</p>
          <p className="text-[10px] text-content-secondary font-medium">Avg Evidence Completeness</p>
        </div>
        <div className="bg-white border border-border-default rounded-lg p-3.5">
          <p className="text-2xl font-heading font-bold text-content-primary">{summary.length}</p>
          <p className="text-[10px] text-content-secondary font-medium">Objectives Tracked</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Objective Evidence Health */}
        <div className="bg-white border border-border-default rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={14} className="text-brand-primary" />
            <h3 className="font-heading font-semibold text-sm text-content-primary">Evidence Health by Objective</h3>
          </div>
          <div className="space-y-2">
            {summary.map(s => (
              <div key={s.objective_id} data-testid={`evidence-health-${s.objective_id}`}
                onClick={() => loadEvidence(s.objective_id)}
                className={`p-2.5 rounded-lg cursor-pointer transition-all ${selectedObj === s.objective_id ? 'bg-brand-primary/5 border border-brand-primary/20' : 'hover:bg-surface-muted'}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-mono text-content-disabled">{s.objective_id}</span>
                    <span className="text-[10px] font-semibold text-content-primary truncate">{s.title.slice(0, 30)}</span>
                  </div>
                  <span className="text-[9px] text-content-disabled">{s.evidence_count}/{s.expected_count}</span>
                </div>
                <HealthBar pct={s.completeness_pct} />
              </div>
            ))}
          </div>
        </div>

        {/* Evidence Files Panel */}
        <div className="lg:col-span-2 bg-white border border-border-default rounded-lg p-4">
          {selectedObj ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-sm text-content-primary">Evidence for {selectedObj}</h3>
                <div className="flex items-center gap-2">
                  <select value={evType} onChange={e => setEvType(e.target.value)} className="px-2 py-1 border border-border-default rounded text-[10px]">
                    <option value="">Type...</option>
                    <option value="audit_report">Audit Report</option>
                    <option value="utility_bill">Utility Bill</option>
                    <option value="certificate">Certificate</option>
                    <option value="inspection_report">Inspection Report</option>
                    <option value="policy_document">Policy Document</option>
                    <option value="supporting_document">Supporting Document</option>
                  </select>
                  <input type="text" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description..." className="px-2 py-1 border border-border-default rounded text-[10px] w-32" />
                  <label data-testid="upload-evidence-btn" className={`px-3 py-1.5 text-[10px] bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover cursor-pointer flex items-center gap-1 ${uploading ? 'opacity-50' : ''}`}>
                    {uploading ? <SpinnerGap size={11} className="animate-spin" /> : <FileArrowUp size={11} />}
                    Upload
                    <input type="file" className="hidden" onChange={upload} disabled={uploading} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xlsx,.csv" />
                  </label>
                </div>
              </div>
              {loadingEvidence ? (
                <div className="text-center py-8"><SpinnerGap size={20} className="animate-spin text-brand-primary mx-auto" /></div>
              ) : evidence.length > 0 ? (
                <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                  {evidence.map(doc => {
                    const Icon = getFileIcon(doc.file_name);
                    return (
                      <div key={doc.id} data-testid={`evidence-file-${doc.id}`} className="flex items-center justify-between p-2.5 bg-surface-default rounded-lg group">
                        <div className="flex items-center gap-2.5 flex-1 min-w-0">
                          <Icon size={16} className="text-brand-primary flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold text-content-primary truncate">{doc.file_name}</p>
                            <p className="text-[9px] text-content-disabled">{doc.evidence_type?.replace(/_/g, ' ')} | by {doc.uploaded_by_name} | {new Date(doc.upload_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 rounded flex items-center justify-center text-content-disabled hover:text-brand-primary hover:bg-brand-primary/10"><Eye size={12} /></a>
                          <button onClick={() => deleteEvidence(doc.id)} className="w-6 h-6 rounded flex items-center justify-center text-content-disabled hover:text-status-alert hover:bg-status-alert-bg"><Trash size={12} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10 text-xs text-content-disabled">
                  <FileArrowUp size={28} className="mx-auto mb-2 text-content-disabled" />
                  No evidence uploaded for this objective yet
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16 text-xs text-content-disabled">
              <ShieldCheck size={32} className="mx-auto mb-2" />
              Select an objective from the left to view & upload evidence
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
